import axios from 'axios';
import { getSession } from '../api/supabase';
import { buildDedupKey, dedupedRequest } from './requestDedup';

const apiCallTracker = {};
const THROTTLE_WINDOW_MS = 3000; // 3 second throttle window
const ADMIN_THROTTLE_WINDOW_MS = 2000; // 2 second throttle window for admin endpoints

// Create an API client (authenticated or unauthenticated)
export const createApiClient = async (requireAuth = true) => {
  try {
    // Get auth token
    const session = await getSession();
    const token = session?.access_token;

    // Determine the API URL, prioritizing environment variables
    const apiUrl = process.env.REACT_APP_API_BASE_URL ||
      (process.env.NODE_ENV === 'development'
        ? 'http://localhost:5000/api'
        : '/api');

    console.log("API Client Creation Details:", {
      token: token ? 'Token Present' : 'No Token',
      apiUrl,
      nodeEnv: process.env.NODE_ENV,
      envApiBaseUrl: process.env.REACT_APP_API_BASE_URL,
      requireAuth
    });

    if (requireAuth && !token) {
      console.error('No authentication token available for API request');
      throw new Error('Authentication required');
    }

    // Create axios instance with conditional auth
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return axios.create({
      baseURL: apiUrl,
      headers,
      timeout: 60000
    });
  } catch (error) {
    console.error('Error creating API client:', error);
    throw error;
  }
};

/**
 * Make an API request (authenticated or unauthenticated based on endpoint)
 * @param {string} endpoint - API endpoint (without leading slash)
 * @param {Object} options - Request options
 * @returns {Promise} Promise resolving to response data
 */
export const apiRequest = async (endpoint, options = {}) => {
  const {
    method = 'GET',
    data = null,
    params = null,
    timeout = 60000, // 60 seconds default timeout
    skipThrottle = false, // Add option to skip throttle
    requireAuth = null // Allow explicit override
  } = options;

  // Determine if authentication is required for this endpoint
  const unauthenticatedEndpoints = [
    '/accept-invite/',
    '/health',
    '/public/'
  ];
  
  const needsAuth = requireAuth !== null ? requireAuth : 
    !unauthenticatedEndpoints.some(pattern => endpoint.includes(pattern));

  // Check throttling unless explicitly skipped or for certain endpoints.
  //
  // Note: the throttle keys on `${method}:${endpoint}` WITHOUT query params,
  // so two legitimate requests to the same endpoint with different params
  // within 3s also get rejected. The in-flight dedup in requestDedup.js is
  // the real protection against double-fires; this throttle is largely
  // defensive against runaway loops and should be exempted for any endpoint
  // a component might re-fetch on a tight cadence (e.g. React StrictMode's
  // double-mount in dev, refresh buttons, optimistic updates).
  const noThrottleEndpoints = [
    '/accept-invite/',
    '/health',
    '/public/',
    '/preview',
    '/lineage/',
    '/tables/',
    '/notifications',
    '/user/notification-preferences',
    '/teams',
    '/profiles',
    '/dashboard/', // Dashboard analytics endpoints should load in parallel during dashboard initialization
    '/automation/activity' // Activity panel refresh button + StrictMode double-mount
  ];
  
  const shouldSkipThrottle = skipThrottle || 
    noThrottleEndpoints.some(pattern => endpoint.includes(pattern));

  if (!shouldSkipThrottle) {
    const now = Date.now();
    const key = `${method}:${endpoint}`;
    const lastCall = apiCallTracker[key];
    
    // Use shorter throttle window for admin endpoints
    const isAdminEndpoint = endpoint.startsWith('/admin') || endpoint.startsWith('admin');
    const throttleWindow = isAdminEndpoint ? ADMIN_THROTTLE_WINDOW_MS : THROTTLE_WINDOW_MS;

    if (lastCall && now - lastCall < throttleWindow) {
      console.log(`Throttling request to ${endpoint} - too recent (${throttleWindow}ms window)`);
      return Promise.reject({
        throttled: true,
        message: 'Request throttled - too frequent'
      });
    }

    // Track this call
    apiCallTracker[key] = now;
  }

  // Dedup via shared cross-library map (utils/requestDedup.js). Mutations
  // pass dedupKey === null and bypass dedup entirely.
  const dedupKey = buildDedupKey(method, endpoint, params);

  return dedupedRequest(dedupKey, async () => {
    try {
      console.log(`Making ${method} request to ${endpoint} (auth required: ${needsAuth})`);
      const client = await createApiClient(needsAuth);

      // Add these new debug logging lines
      const session = await getSession();
      const token = session?.access_token;
      console.log(`Auth token available for ${endpoint}:`, !!token);

      // Override client timeout if specified
      if (timeout !== 60000) {
        client.defaults.timeout = timeout;
      }

      const response = await client({
        method,
        url: endpoint,
        data,
        params
      });

      console.log(`Response from ${endpoint}:`, response.data);
      return response.data;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  });
};